import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  CCard,
  CCardBody,
  CRow,
  CCol,
  CCardTitle,
  CInput,
  CLabel,
  CCardHeader,
  CCardFooter,
  CFormGroup,
  CInputCheckbox,
  CInputGroup,
  CInputGroupPrepend,
  CInputGroupText,
  CSelect,
} from '@coreui/react';
import PageLayout from '../../components/PageLayout';
import { useHistory, useParams } from 'react-router';
import { useMutation, useQuery } from 'react-query';
import { loadGroupWithExtraData, updateGroupWithMarkups } from '../../services/BackendService';
import { extractErrorMessage, onlyPositiveInteger, findErrorFromValidation } from '../../helpers';
import { IGroupWithExtraData, IMarkup } from '../customers/types';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import _t from 'counterpart';
import { OrderRoutingAdapterSelector } from '../customers/CustomerWalletPopup';
import toast from 'react-hot-toast';
import { ITradeCommission, IGroupRowData, ISwap, swapDays, swapModes } from './types';
import CIcon from '@coreui/icons-react';
import cilArrowUp from '../../icons/cil-chevron-arrow-up.svg';
import cilArrowDown from '../../icons/cil-chevron-arrow-down.svg';

const GroupPage = () => {
  const params = useParams();
  const [groupData, setGroupData] = useState<Array<IGroupRowData>>([]);
  const [leverage, setLeverage] = useState<string>();
  const [marginCallLevel, setMarginCallLevel] = useState<string>();
  const [stopOutLevel, setStopOutLevel] = useState<string>();
  const [adapterName, setAdapterName] = useState<string>('');
  const [disableSwapCharges, setDisableSwapCharges] = useState<boolean>(false);

  const { id } = params as any;

  const loadGroupWithExtraDataQuery = useQuery(['load-group-with-markups', id], () => loadGroupWithExtraData(id), {
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
  });

  const groupWithExtraData = loadGroupWithExtraDataQuery.data;
  const history = useHistory();
  const name = groupWithExtraData?.name;

  const combineData = (markups: Array<IMarkup>, commissions: Array<ITradeCommission>, swaps: Array<ISwap>) => {
    const max = Math.max(markups.length, commissions.length, swaps.length);
    const rowData: Array<IGroupRowData> = [];

    for (let i = 0; i < max; i++) {
      const markup = markups[i];
      const commission = commissions.find((commission) => commission.symbol === markup.instrumentSymbol);
      const swap = swaps.find((swap) => swap.symbol === markup.instrumentSymbol);

      const groupRowData: IGroupRowData = {
        ...markup,
        ...(commission as ITradeCommission),
        ...(swap as ISwap),
        symbol: markup ? markup.instrumentSymbol : (commission as ITradeCommission).symbol,
      };

      rowData.push({ ...groupRowData, expanded: false });
    }

    return rowData;
  };

  useEffect(() => {
    if (groupWithExtraData && loadGroupWithExtraDataQuery.isSuccess) {
      const markups = groupWithExtraData.markups;
      const commissions = groupWithExtraData.commissions;
      const swaps = groupWithExtraData.swaps;

      const rowData = combineData(markups, commissions, swaps);
      setGroupData(rowData);

      setLeverage(groupWithExtraData.leverage.toString());
      setMarginCallLevel(groupWithExtraData.marginCallLevel.toString());
      setStopOutLevel(groupWithExtraData.stopOutLevel.toString());
      setAdapterName(groupWithExtraData.routingAdapterName);
      setDisableSwapCharges(groupWithExtraData.disableSwapCharges);
    }
  }, [groupWithExtraData, loadGroupWithExtraDataQuery.isSuccess, setLeverage]);

  const updateGroupWithMarkupsMutation = useMutation(
    (updatedGroup: IGroupWithExtraData) =>
      updateGroupWithMarkups(
        updatedGroup.id,
        updatedGroup.leverage,
        updatedGroup.marginCallLevel,
        updatedGroup.stopOutLevel,
        updatedGroup.routingAdapterName,
        updatedGroup.markups,
        updatedGroup.commissions,
        updatedGroup.disableSwapCharges,
        updatedGroup.swaps
      ),
    {
      onSuccess: () => {
        loadGroupWithExtraDataQuery.refetch();
        toast.success(_t('groups.group-successfully-update'));
        history.push('/groups');
      },
      onError: (e: any) => {
        const error = extractErrorMessage(e);
        toast.error(error);
      },
    }
  );

  const handleInputChange = useCallback(
    (symbol: string, field: string, value: string) => {
      const updateRows = groupData.map((item) => {
        if (item.symbol === symbol) {
          return { ...item, [field]: value };
        }
        return item;
      });

      setGroupData(updateRows);
    },
    [groupData]
  );

  const setRoutingAdapterName = (adapterName: string) => {
    setAdapterName(adapterName);
  };

  const updateGroup = () => {
    if (groupWithExtraData) {
      const markups: Array<IMarkup> = groupData.map((row) => {
        return {
          groupId: groupWithExtraData.id.toString(),
          instrumentSymbol: row.symbol,
          ask: Number(row.ask),
          bid: Number(row.bid),
        };
      });

      const commissions: Array<ITradeCommission> = groupData.map((row) => {
        return {
          groupId: groupWithExtraData.id.toString(),
          symbol: row.symbol,
          open: Number(row.open),
          close: Number(row.close),
        };
      });

      const swaps: Array<ISwap> = groupData.map((row) => {
        return {
          groupId: groupWithExtraData.id.toString(),
          symbol: row.symbol,
          swapLong: Number(row.swapLong),
          swapShort: Number(row.swapShort),
          swapMode: row.swapMode,
          swap3DayWeekday: row.swap3DayWeekday !== null ? Number(row.swap3DayWeekday) : null,
        };
      });

      const updateGroup: IGroupWithExtraData = {
        ...groupWithExtraData,
        id: groupWithExtraData?.id,
        leverage: Number(leverage),
        marginCallLevel: Number(marginCallLevel),
        stopOutLevel: Number(stopOutLevel),
        routingAdapterName: adapterName,
        markups,
        commissions,
        swaps,
      };
      updateGroupWithMarkupsMutation.mutate(updateGroup);
    }
  };

  const onSetLeverage = (e: FormEvent) => {
    const target = e.target as HTMLInputElement;
    const value = target.value;

    if (onlyPositiveInteger(value)) {
      setLeverage(value);
    }
  };

  const onSetStopoutLevel = (e: FormEvent) => {
    const target = e.target as HTMLInputElement;
    const value = target.value;

    if (onlyPositiveInteger(value)) {
      setStopOutLevel(value);
    }
  };

  const toggleDisableSwapCharges = () => {
    setDisableSwapCharges(!disableSwapCharges);
  };

  const onMarginLevel = (e: FormEvent) => {
    const target = e.target as HTMLInputElement;
    const value = target.value;

    if (onlyPositiveInteger(value)) {
      setMarginCallLevel(value);
    }
  };

  const findError = (paramName: string) => {
    return findErrorFromValidation(updateGroupWithMarkupsMutation.error, paramName);
  };

  const handleSelectChange = (symbol: string, field: keyof IGroupRowData, value: any) => {
    setGroupData((prev) => prev.map((item) => (item.symbol === symbol ? { ...item, [field]: value } : item)));
  };
  return (
    <PageLayout title={name || ''}>
      <CRow>
        <CCol>
          <CCard>
            <CCardHeader>
              <CCardTitle>{_t('groups.trading-and-risk')}</CCardTitle>
              <CRow>
                <CCol md={4}>
                  <CFormGroup>
                    <CLabel>{_t('groups.default-leverage')}</CLabel>
                    <CInput value={leverage} onChange={onSetLeverage} />
                  </CFormGroup>
                </CCol>
                <CCol md={4}>
                  <CFormGroup>
                    <CLabel>{_t('groups.stop-out-level')}</CLabel>
                    <CInput value={stopOutLevel} onChange={onSetStopoutLevel} />
                  </CFormGroup>
                </CCol>
                <CCol md={4}>
                  <CFormGroup>
                    <CLabel>{_t('groups.margin-call-level')}</CLabel>
                    <CInput value={marginCallLevel} onChange={onMarginLevel} />
                  </CFormGroup>
                </CCol>
              </CRow>
              <CFormGroup>
                <CLabel htmlFor="nf-type">{_t('wallet-modal.order-routing-adapter')}</CLabel>
                <OrderRoutingAdapterSelector onChange={setRoutingAdapterName} value={adapterName} />
                {findError('adapterName') && <CLabel className="text-danger">{findError('adapterName')}</CLabel>}
              </CFormGroup>
              <CFormGroup>
                <div className="form-check form-check-inline">
                  <CInputCheckbox
                    className="form-check-input"
                    type="checkbox"
                    id="is-demo"
                    onChange={toggleDisableSwapCharges}
                    checked={disableSwapCharges}
                  />
                  <span>{_t('groups.disable-swap-charges')}</span>
                </div>
              </CFormGroup>
            </CCardHeader>
            <CCardBody>
              <CCardTitle style={{ display: 'block', clear: 'left', marginBottom: '15px' }}>
                {_t('instruments.title')}
              </CCardTitle>
              {groupData.map((row, index) => (
                <CCard
                  key={row.symbol}
                  className="mb-3"
                  style={{ border: '1px solid #E5E5E5', backgroundColor: row.expanded ? '#F7F7FA' : '#fff' }}
                >
                  <CCardHeader
                    className="d-flex justify-content-between align-items-center"
                    onClick={() =>
                      setGroupData((prev) =>
                        prev.map((item, i) => ({
                          ...item,
                          expanded: i === index ? !item.expanded : false,
                        }))
                      )
                    }
                    style={{ cursor: 'pointer', backgroundColor: row.expanded ? '#F7F7FA' : '#fff' }}
                  >
                    <strong>{row.symbol}</strong>
                    <span>{row.expanded ? <CIcon src={cilArrowUp} /> : <CIcon src={cilArrowDown} />}</span>
                  </CCardHeader>
                  {row.expanded && (
                    <CCardBody>
                      <CRow>
                        <CCol md={3}>
                          <CLabel>{_t('groups.markup-buy')}</CLabel>
                          <CInput
                            value={row.bid}
                            type="number"
                            onChange={(e) => handleInputChange(row.symbol, 'bid', (e.target as HTMLInputElement).value)}
                          />
                        </CCol>
                        <CCol md={3}>
                          <CLabel>{_t('groups.markup-sell')}</CLabel>
                          <CInput
                            value={row.ask}
                            type="number"
                            onChange={(e) => handleInputChange(row.symbol, 'ask', (e.target as HTMLInputElement).value)}
                          />
                        </CCol>
                        <CCol md={3}>
                          <CLabel>{_t('groups.commission-open')}</CLabel>
                          <CInputGroup>
                            <CInputGroupPrepend>
                              <CInputGroupText>$</CInputGroupText>
                            </CInputGroupPrepend>
                            <CInput
                              value={row.open}
                              type="number"
                              onChange={(e) =>
                                handleInputChange(row.symbol, 'open', (e.target as HTMLInputElement).value)
                              }
                            />
                          </CInputGroup>
                        </CCol>
                        <CCol md={3}>
                          <CLabel>{_t('groups.commission-close')}</CLabel>
                          <CInputGroup>
                            <CInputGroupPrepend>
                              <CInputGroupText>$</CInputGroupText>
                            </CInputGroupPrepend>
                            <CInput
                              value={row.close}
                              type="number"
                              onChange={(e) =>
                                handleInputChange(row.symbol, 'close', (e.target as HTMLInputElement).value)
                              }
                            />
                          </CInputGroup>
                        </CCol>
                      </CRow>

                      <CRow className="mt-3">
                        <CCol md={3}>
                          <CLabel>{_t('groups.swap-long')}</CLabel>
                          <CInput
                            value={row.swapLong}
                            type="number"
                            onChange={(e) =>
                              handleInputChange(row.symbol, 'swapLong', (e.target as HTMLInputElement).value)
                            }
                          />
                        </CCol>
                        <CCol md={3}>
                          <CLabel>{_t('groups.swap-short')}</CLabel>
                          <CInput
                            value={row.swapShort}
                            type="number"
                            onChange={(e) =>
                              handleInputChange(row.symbol, 'swapShort', (e.target as HTMLInputElement).value)
                            }
                          />
                        </CCol>
                        <CCol md={3}>
                          <CLabel>{_t('groups.swap-mode')}</CLabel>
                          <CSelect
                            value={row.swapMode}
                            onChange={(e) =>
                              handleSelectChange(row.symbol, 'swapMode', (e.target as HTMLInputElement).value)
                            }
                          >
                            {swapModes.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </CSelect>
                        </CCol>
                        <CCol md={3}>
                          <CLabel>{_t('groups.swap-3day-weekday')}</CLabel>{' '}
                          <CSelect
                            value={row.swap3DayWeekday === null ? 'null' : row.swap3DayWeekday}
                            onChange={(e) =>
                              handleSelectChange(row.symbol, 'swap3DayWeekday', (e.target as HTMLInputElement).value)
                            }
                          >
                            {swapDays.map((opt) => (
                              <option key={opt.value ?? 'null'} value={opt.value === null ? 'null' : opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </CSelect>
                        </CCol>
                      </CRow>
                    </CCardBody>
                  )}
                </CCard>
              ))}
            </CCardBody>
            <CCardFooter>
              <CRow>
                <CCol>
                  <ButtonWithLoader
                    isLoading={updateGroupWithMarkupsMutation.isLoading}
                    onClick={updateGroup}
                    buttonColor="primary"
                    spinnerColor="secondary"
                    title={_t('action.save')}
                    className="group-page-save-button"
                  />
                </CCol>
              </CRow>
            </CCardFooter>
          </CCard>
        </CCol>
      </CRow>
    </PageLayout>
  );
};

export default GroupPage;
